import { ARCHITECTURE, WORKLOAD_DOMAIN_TYPE } from './pcsizer.enums';
import { PcsSizingEngineResponse } from './pcs-sizing-engine-response.model';

export namespace VcfSizingOutput {
    export class Summary {
        vcfDeploymentInfraSummary: InfraSummary = {} as InfraSummary;
        productInfo: ProductInfo = {} as ProductInfo;
        workloadDomains?: WorkloadDomain[];
        managementDomain: ManagementDomain = {} as ManagementDomain;
    }
    export class ManagementDomain{
        domainInfraSummary: InfraSummary = {} as InfraSummary;
        architecture: string | null | undefined;
        managementComponents: PcsSizingEngineResponse.SoftwareComponent[] = [];
        workloadDomainComponents?: PcsSizingEngineResponse.SoftwareComponent[];
        isStretched?: boolean; // Indicates if management domain is stretched (based on workload domains)
        witnessVms?: number; // Number of witness VMs (not multiplied for stretched clusters)
    }
    export class WorkloadDomain{
        ownedStorageClusters?: ClusterSummary[];
        id: string = "";
        name: string = "";
        domainComponents?: PcsSizingEngineResponse.SoftwareComponent[];
        domainDetails: DomainDetails = {} as DomainDetails;
        workloads: Workload[] = [];
        workloadDomainType?: WORKLOAD_DOMAIN_TYPE;
        // Deduped, ownership-aware domain totals (see aggregateDomainInfra) - absent on summaries
        // saved before it existed, where consumers fall back to summing workloadInfraSummary.
        domainInfra?: InfraSummaryNumberValues;
    }
    export class Workload{
        name?: string;
        type?: string;
        subType?: string;
        vSANArchitecture?: string | null; // ESA or OSA
        workloadInfraSummary: InfraSummaryNumberValues = {} as InfraSummaryNumberValues;
        clusterType?: string; // 'standard' | 'stretched' | 'twoNode'
        // Computed via isStretchedByRecommendation() at summary-build time (same pcs-domain source
        // of truth as ClusterSummary.isStretched below) - mirrors ManagementDomain.isStretched above.
        // Consumers should read this directly rather than re-deriving stretched-ness from clusterType.
        isStretched?: boolean;
        witnessVms?: number; // Number of witness VMs (not multiplied for stretched clusters)
        clusters: ClusterSummary[] = []; // Per-cluster breakdown (additive, workloadInfraSummary stays the source of truth for aggregates)
        deploymentType: 'HCI' | 'dHCI' = 'HCI'; // Echoes workload input's vcfDeploymentInputs.deploymentType
    }
    export interface ConnectedWorkloadSummary {
        workloadId: string;
        domainId: string;
        displayName: string;
        vmCount?: number;
    }
    export class ClusterSummary {
        sourceRecommendation?: { requirementsId: string; workloadId: string };
        canonicalStorageClusterId?: string;
        sizingUnavailable?: boolean;
        id: string = "";                 // `${workloadId}-${clusterIndex}`
        index: number = 0;               // index within the filtered (non-witness/PRIMARY-only) cluster list
        name: string = "";                // `${clusterName}-${clusterSplitName}` via buildClusterDisplayName(), falling back to whichever single field is present
        // The engine-side Cluster.clusterId this card was built from - the one key stable across
        // both card builders (buildClusterSummaries / buildCarouselClusterSummaries), used to map a
        // rendered card back to its tree leaf (see resolveClusterLocation) for the Details button.
        clusterId?: string;
        hosts: number = 0;
        vms: number = 0;
        cores: number = 0;                // per-host cores * hosts
        memory: NumValueUnit = { value: 0, unit: "GB" };       // per-host memory * hosts
        rawStorage: NumValueUnit = { value: 0, unit: "GB" };
        memoryUsedByAppsPerHost: NumValueUnit = { value: 0, unit: "GB" }; // NOT multiplied by hosts
        capacityDrivesPerHost: number = 0;
        driveSize: NumValueUnit = { value: 0, unit: "GB" };
        vsanArchitecture?: string | null;
        // vSAN OSA-only (disk groups don't apply to ESA) - undefined for ESA clusters
        diskGroupsCount?: number;
        cacheTierDriveSize?: NumValueUnit;
        // dHCI-only, forward-ready, always undefined until backend support exists
        clusterRole?: 'COMPUTE' | 'STORAGE';
        // Raw dHCI split-cluster role from the backend ('CON'/'SON'/'HCI') — drives the header role
        // badge (HCI/Compute/Storage) directly, independent of isVsanMaxEnabled/clusterRole.
        clusterType?: 'CON' | 'SON' | 'HCI';
        // The engine-side Cluster.clusterName alone (unlike `name`, never decorated with
        // clusterSplitName) - the grouping key for a threshold-triggered cluster split ("Cluster 1",
        // "Cluster 2", ... all share one clusterBaseName). Used to collapse a split cluster's
        // entries into one tree-nav leaf and to aggregate its cards on the detail page.
        clusterBaseName?: string;
        connectedComputeClusterNames?: string[];
        // Workload names sharing this cluster (from Cluster.connectedWorkloads) — rendered as
        // badges in the "Connected Workloads" card section.
        connectedWorkloadNames?: string[];
        connectedWorkloads?: ConnectedWorkloadSummary[];
        // COMPUTE-role only: storage cluster name(s) this compute cluster mounts (from
        // Cluster.connectedClusters) — rendered as badges in "Mounted Storage Clusters".
        mountedStorageClusterNames?: string[];
        // True when this cluster's chassis is owned by a different workload domain than the one
        // currently being viewed (canonical ownership, or the chassis.owner fallback) — drives the
        // Cross-WLD badge next to the Compute/Storage role badge.
        isCrossWorkloadDomainCluster?: boolean;
        // True when this card represents ONE site of a stretched workload (Secondary is filtered
        // out as a duplicate-in-practice mirror of Primary) - drives the "x2" indicator on
        // ClusterCardComponent's metric rows, since the values shown are per-site, not domain-wide.
        isStretched?: boolean;
        // Set only on the per-site cards of a stretched workload's own cluster page (one card per
        // Primary / Secondary / Witness site). A card with a siteType already shows its own site's
        // values, so ClusterCardComponent skips the "x2" indicator for it; a card with no siteType
        // (legacy / other views) keeps the "x2" behaviour.
        siteType?: 'PRIMARY' | 'SECONDARY' | 'WITNESS';
        // WITNESS cards only: appliances grouped by type (witness has no host/VM/core/RAM metrics).
        witnessAppliances?: { applianceType: string; count: number }[];
        // WITNESS cards only: version-specific deploy-instructions link (set by the owning page, which knows the project version).
        witnessDocumentationUrl?: string;
        // Domain-level optional workload-domain components (NSX Edge, HCX, etc.), attached only to
        // the domain's target cluster (first HCI, else first CON) via resolveComponentsTargetClusterId()
        // - undefined on every other cluster card, including other splits of the same base cluster.
        hostedDomainComponents?: PcsSizingEngineResponse.SoftwareComponent[];
    }
    export class DomainDetails{
        numberOfRacks?: number;
        numberOfClusters?: number;
        numberofTorSwitch?: number;
        numberOfInterRackSwitch?: number;
        numberOfManagementSwitch?: number;
        numberOfVMs?: number;
        numberOfHosts?: number;
    }
    export class ProductInfo {
        productName?: string;
        version?: string;
        availableStorageEntitlement?: ValueUnit;
        entitlementDescription: string = "1 TiB / Core";
    }
    export class ValueUnit {
        value?: string | number;
        unit?: string;
    }
    export class NumValueUnit {
        value: number = 0;
        unit?: string = "";
    }
    export class InfraSummary {
        // Undefined on legacy summaries; false means the scope has no storage-bearing clusters.
        hasStorage?: boolean;
        hosts: ValueUnit = { value: "0", unit: "Hosts"};
        cores: ValueUnit = { value: "0", unit: "Cores"};
        memory: ValueUnit = { value: "0", unit: "GB"};
        vms: ValueUnit = { value: "0", unit: "VMs"};
        rawStorage: ValueUnit = { value: "0", unit: "TB"};
        usableStorage: ValueUnit = { value: "0", unit: "TB"};          // rawStorage minus overheads
        usedByWorkloadsStorage: ValueUnit = { value: "0", unit: "TB"}; // consumed within usableStorage
    }
    export class InfraSummaryNumberValues {
        // Undefined on legacy summaries; false means the scope has no storage-bearing clusters.
        hasStorage?: boolean;
        hosts: NumValueUnit = { value: 0, unit: "Hosts"};
        cores: NumValueUnit = { value: 0, unit: "Cores"};
        memory: NumValueUnit = { value: 0, unit: "GB"};
        vms: NumValueUnit = { value: 0, unit: "VMs"};
        rawStorage: NumValueUnit = { value: 0, unit: "TB"};
        usableStorage: NumValueUnit = { value: 0, unit: "TB"};          // rawStorage minus overheads
        usedByWorkloadsStorage: NumValueUnit = { value: 0, unit: "TB"}; // consumed within usableStorage
    }
    export class Recommendation { }
}